import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./axiosClient";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx client errors (잘못된 요청, 인증 실패 등)
        if (error instanceof ApiError && error.isClientError()) {
          return false;
        }
        // 5xx 서버 에러 및 네트워크 에러는 최대 3번까지 재시도
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Query key factory pattern
export const queryKeys = {
  all: ["all"] as const,
  users: {
    all: ["users"] as const,
    lists: () => [...queryKeys.users.all, "list"] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },
  orders: {
    all: ["orders"] as const,
    lists: () => [...queryKeys.orders.all, "list"] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.orders.lists(), filters] as const,
    details: () => [...queryKeys.orders.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.orders.details(), id] as const,
  },
  prismConfig: {
    all: ["prismConfig"] as const,
    list: () => [...queryKeys.prismConfig.all, "list"] as const,
  },
  anomalyDetectionRules: {
    all: ["anomalyDetectionRules"] as const,
    lists: () => [...queryKeys.anomalyDetectionRules.all, "list"] as const,
    list: () => [...queryKeys.anomalyDetectionRules.lists()] as const,
    details: () => [...queryKeys.anomalyDetectionRules.all, "detail"] as const,
    detail: (id: number) => [...queryKeys.anomalyDetectionRules.details(), id] as const,
    channels: () => [...queryKeys.anomalyDetectionRules.all, "channels"] as const,
    checkTypes: () => [...queryKeys.anomalyDetectionRules.all, "checkTypes"] as const,
  },
  channelDelayThresholds: {
    all: ["channelDelayThresholds"] as const,
    list: () => [...queryKeys.channelDelayThresholds.all, "list"] as const,
  },
  facebookResourceTargetDates: {
    all: ["facebookResourceTargetDates"] as const,
    state: () => [...queryKeys.facebookResourceTargetDates.all, "state"] as const,
  },
  collectionStatus: {
    all: ["collectionStatus"] as const,
    status: (params?: Record<string, unknown>) =>
      [...queryKeys.collectionStatus.all, "status", params] as const,
  },
  collectionErrors: {
    all: ["collectionErrors"] as const,
    errors: (dateRange: string) =>
      [...queryKeys.collectionErrors.all, dateRange] as const,
  },
  collectionSizes: {
    all: ["collectionSizes"] as const,
    search: (params: Record<string, unknown>) =>
      [...queryKeys.collectionSizes.all, "search", params] as const,
  },
  e2eTests: {
    all: ["e2eTests"] as const,
    dashboard: () => [...queryKeys.e2eTests.all, "dashboard"] as const,
  },
  errorPatterns: {
    all: ["errorPatterns"] as const,
    list: () => [...queryKeys.errorPatterns.all, "list"] as const,
  },
  organizations: {
    all: ["organizations"] as const,
    list: () => [...queryKeys.organizations.all, "list"] as const,
  },
  dataForwarderPolicies: {
    all: ["dataForwarderPolicies"] as const,
    lists: () => [...queryKeys.dataForwarderPolicies.all, "list"] as const,
    list: () => [...queryKeys.dataForwarderPolicies.lists()] as const,
    details: () => [...queryKeys.dataForwarderPolicies.all, "detail"] as const,
    detail: (id: number) => [...queryKeys.dataForwarderPolicies.details(), id] as const,
    channels: () => [...queryKeys.dataForwarderPolicies.all, "channels"] as const,
    regions: () => [...queryKeys.dataForwarderPolicies.all, "regions"] as const,
  },
  universalThrottlingPolicies: {
    all: ["universalThrottlingPolicies"] as const,
    lists: () => [...queryKeys.universalThrottlingPolicies.all, "list"] as const,
    list: () => [...queryKeys.universalThrottlingPolicies.lists()] as const,
    details: () => [...queryKeys.universalThrottlingPolicies.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.universalThrottlingPolicies.details(), id] as const,
    channels: () => [...queryKeys.universalThrottlingPolicies.all, "channels"] as const,
  },
  monthlyPerformanceSlack: {
    all: ["monthlyPerformanceSlack"] as const,
    channel: () => [...queryKeys.monthlyPerformanceSlack.all, "channel"] as const,
  },
  madupIndustryMapping: {
    all: ["madupIndustryMapping"] as const,
    list: () => [...queryKeys.madupIndustryMapping.all, "list"] as const,
  },
  // 추가 도메인은 같은 패턴으로 확장
};
